<template>
  <div ref="container"
       :class="containerClass">
    <div class="pdfviewer__content-wrapper mx-auto" ref="wrapper" v-bind:style="{ width: `${contentWidth}px` }">
      <div class="pdfviewer__content mx-auto"
           v-for="(content) in contents"
           v-bind:key="content.id"
           v-bind:style="{ width: `${content.width}px`, height: `${content.height}px` }"
           ref="content">
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent } from "vue";
  import { xhrRequest, xhrErrors } from "utils/xhr";
  import Spinner from "utils/spinner";

  const onCreated = function () {
    this._pdfDocument = null;
    this._pdfPages = [];
    this._renderTasks = [];
    this._deferred = null
    this._resizeTimer = null;
    this._scrollTimer = null;
    this._loadedPages = 0;

    if (!window.pdfjsLib) {
      throw "pdfjsLib is not loaded."
    }
    //pdfjsLib.GlobalWorkerOptions.workerPort = new Worker(new URL(/* webpackChunkName: "pdf.worker" */ "pdfjs-dist/build/pdf.worker.js", import.meta.url));
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = this.workerSrc;
    this.pdfjsLib = pdfjsLib;
  }

  const onDestroyed = function () {
    this.removeEvent();
    this.destroy();
  }

  const onMounted = function () {
    this.initDocument(this.url);

    this.addEvent();
    this.addCursorEvent(this.scrollContainer);
  }

  const methods = {
    /* パブリックメソッド */
    download: function () {
      if (this.error || !this._pdfDocument) {
        return;
      }
      this._pdfDocument.getData().then((data) => {
        let blob = new Blob([data], { type: 'application/pdf' });
        let link = document.createElement('a');
        link.download = this.fileName;
        link.target = '_blank';

        if (window.URL && window.URL.createObjectURL) {
          let url = window.URL.createObjectURL(blob);
          link.href = window.URL.createObjectURL(blob);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      });
    },
    getPage: function () {
      return this.currentPage;
    },
    setPage: function (page) {
      this.addPages(page);
      if (this._scrollTimer) return;
      this._deferred && this._deferred.then(() => {
        this.scrollTo(page);
      });
    },
    /* プライベートメソッド */
    addEvent: function () {
      window.addEventListener("resize", this.onResize);
      this.scrollContainer.addEventListener("scroll", this.onScroll);
    },
    removeEvent: function () {
      this.scrollContainer.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onResize);
    },
    addCursorEvent: function (target) {
      target.addEventListener("pointerdown", this.bindGrab);
    },
    removeCursorEvent: function (target) {
      target.removeEventListener("pointerdown", this.bindGrab);
    },
    bindGrab: function (e) {
      if (!(this.cursorMode === "hand")) return;
      this.scrollContainer.style.touchAction = "none";
      this.scrollContainer.classList.add("grabbing");
      this.cursorPosition.x = e.screenX;
      this.cursorPosition.y = e.screenY;
      this.scrollPosition.x = this.scrollContainer.scrollLeft;
      this.scrollPosition.y = this.scrollContainer.scrollTop;
      window.addEventListener("pointermove", this.cursorGrabbing);
      window.addEventListener("pointerup", this.releaseGrab);
    },
    cursorGrabbing: function (e) {
      this.scrollContainer.scrollTo({
        top: this.scrollPosition.y + this.cursorPosition.y - e.screenY,
        left: this.scrollPosition.x + this.cursorPosition.x - e.screenX,
      })
    },
    releaseGrab: function () {
      this.scrollContainer.style.touchAction = "";
      this.scrollContainer.classList.remove("grabbing");
      window.removeEventListener("pointermove", this.cursorGrabbing);
      window.removeEventListener("pointerup", this.releaseGrab);
    },
    getContainerWidth: function () {
      let container = this.scrollContainer;
      if (container == null) return 0;
      return container.clientWidth;
    },
    createCanvas: function (container, content) {
      let canvas = document.createElement("canvas");
      canvas.width = content.width;
      canvas.height = content.height;
      container.innerHTML = '';
      container.appendChild(canvas);
      return canvas;
    },
    addPage: function (pageNo) {
      if (pageNo != this._loadedPages + 1) {
        return;
      }
      this._loadedPages = pageNo;
      return this._pdfDocument.getPage(pageNo).then((page) => {
        let scale = this.contentWidth / page.getViewport({ scale: 100.0 / this.zoom }).width;
        let viewPort = page.getViewport({ scale: scale });
        let content = this.createContent(pageNo, viewPort);
        const index = this.contents.length;
        this.contents = [...this.contents, content];
        this._pdfPages[index] = page;
        this.$nextTick(() => {
          const canvas = this.createCanvas(this.$refs.content[index], content);
          const context = canvas.getContext("2d");
          const params = { canvasContext: context, viewport: viewPort };
          const task = page.render(params).promise;
          this._renderTasks.push(task);
        });
      });
    },
    onScroll: function (e) {
      if (this.error || !this._pdfDocument) {
        return;
      }

      if (this._scrollTimer !== null) {
        clearTimeout(this._scrollTimer);
        this._scrollTimer = null;
      }

      const rect = this.$refs.wrapper.getBoundingClientRect();
      if (Math.floor(rect.bottom) <= this.scrollContainer.clientHeight) {
        this.addPages();
      }
      this.setCurrentPage();

      this._scrollTimer = setTimeout(() => {
        this._scrollTimer = null;
      }, 200);
    },
    setCurrentPage: function () {
      let currentPage = 0;
      const baseLine = Math.floor(this.scrollContainer.clientHeight / 2);
      for (let i = 0; i < this.$refs.content.length; i++) {
        const content = this.$refs.content[i];
        const rect = content.getBoundingClientRect();
        if (rect.top > baseLine) break;
        currentPage = i;
      }
      this.currentPage = currentPage + 1;
    },
    scrollTo: function (pageNo) {
      if (pageNo > this.$refs.content.length) {
        return;
      }

      Promise.all(this._renderTasks).then(() => {
        const index = pageNo - 1;
        const content = this.$refs.content[index];
        const rect = content.getBoundingClientRect();
        const scrollContainer = this.scrollContainer;
        const scrollTop = scrollContainer.scrollTop;
        let top = scrollTop + rect.top;

        scrollContainer.scrollTo({
          behavior: "instant",
          left: 0,
          top
        });
        this.currentPage = pageNo;
      });
    },
    onResize: function (e) {
      if (this.error || !this._pdfDocument) {
        return;
      }
      if (this._resizeTimer) {
        // リサイズが連続して実行されないようにする
        clearTimeout(this._resizeTimer);
        this._resizeTimer = null;
      }

      const sleep = () => new Promise(resolve => setTimeout(resolve, second * 100))
      this._resizeTimer = setTimeout(() => {
        const currentPage = this.currentPage;
        const retry = 20;
        for (let i = 0; i < retry; i++) {
          if (this._renderTasks.length == this._loadedPages) {
            this.recreateCanvases();
            break;
          }
          sleep();
        }
        this.setPage(currentPage);
      }, 200);
    },
    recreateCanvases: function () {
      if (this.contents.length === 0) {
        return;
      }
      this.containerWidth = this.getContainerWidth();
      // レンダリング中はリサイズしない
      Promise.all(this._renderTasks).then(() => {
        let newContents = [];
        for (let i = 0; i < this.contents.length; i++) {
          let page = this._pdfPages[i];
          let scale = this.contentWidth / page.getViewport({ scale: 100.0 / this.zoom }).width;
          let viewPort = page.getViewport({ scale: scale });
          let content = this.createContent(i + 1, viewPort);
          newContents.push(content);
        }
        this.contents = newContents;
        this.$nextTick(() => {
          for (let i = 0; i < newContents.length; i++) {
            let page = this._pdfPages[i];
            const content = newContents[i];
            const canvas = this.createCanvas(this.$refs.content[i], content);
            const context = canvas.getContext("2d");
            const params = { canvasContext: context, viewport: content.viewPort };
            const task = page.render(params).promise;
            this._renderTasks[i] = task;
          }
        });
      });
    },
    createContent: function (pageNo, viewPort) {
      let height = Math.ceil(viewPort.height);
      let width = Math.ceil(viewPort.width);
      return {
        id: `pdf-content-${pageNo}`,
        pageNo: pageNo,
        height,
        width,
        viewPort,
      };
    },
    addPages: function (numPages) {
      let loadedPages = this._loadedPages;
      if (loadedPages >= this.totalPages) {
        return;
      }
      let nextbatch;
      if (numPages != null) {
        if (numPages < loadedPages) return;
        nextbatch = numPages + this.batchSize - 1;
      } else {
        nextbatch = loadedPages + this.batchSize;
      }
      if (nextbatch > this.totalPages) {
        nextbatch = this.totalPages;
      }

      for (let i = loadedPages; i < nextbatch; i++) {
        let nextPage = i + 1;
        if (this._deferred) {
          this._deferred = this._deferred.then(() => this.addPage(nextPage));
        } else {
          this._deferred = this.addPage(nextPage);
        }
      }
    },
    initDocument: function (url) {
      this._deferred = null;
      this._pdfPages = [];
      this._renderTasks = [];
      this._loadedPages = 0;

      this.contents = [];
      this.error = null;

      var loadingTask = this.pdfjsLib.getDocument(url);
      loadingTask.promise.then((pdf) => {
        this._pdfDocument = pdf;
        this.totalPages = pdf.numPages;
        this.currentPage = 1;
        this.containerWidth = this.getContainerWidth();
        
        this.addPages(this.initialPage);
        if (this.initialPage) {
          this._deferred && this._deferred.then(() => {
            this.scrollTo(this.initialPage);
          });
        }
      }, (reason) => {
        // PDF loading error
        console.error(reason);
      });
    },
    destroy: function () {
      if (this._pdfDocument) {
        this._pdfDocument.destroy();
        this._pdfDocument = null;
      }
    }
  };
  const watchers = {
    url: function (newVal, oldVal) {
      this.destroy();
      this.initDocument(newVal);
    },
    currentPage: function (newVal, oldVal) {
      if (this.onCurrentPageChange) {
        // ページ変更イベントを発火
        this.onCurrentPageChange(newVal);
      }
    },
    totalPages: function (newVal, oldVal) {
      if (this.onTotalPagesChange) {
        // ページ変更イベントを発火
        this.onTotalPagesChange(newVal);
      }
    },
    zoom: function (newVal, oldVal) {
      if (newVal != oldVal && newVal > 0 && newVal <= 400) {
        const currentPage = this.currentPage;
        this.recreateCanvases();
        this._deferred && this._deferred.then(() => {
          this.scrollTo(this.currentPage);
        });
        this.setPage(currentPage);
      }
    },
    pages: function (newVal, oldVal) {
      this.setPage(newVal);
    },
    scrollContainer: function (newVal, oldVal) {
      this.removeEvent();
      this.removeCursorEvent(oldVal);
      this.addEvent();
      this.addCursorEvent(newVal);
      this.containerWidth = this.getContainerWidth();
    },
  };
  export default defineComponent({
    data: () => {
      return {
        pdfjsLib: null,
        containerWidth: 0,
        batchSize: 5,
        totalPages: 0,
        currentPage: 1,
        error: null,
        contents: [],
        cursorPosition: {
          x: 0,
          y: 0,
        },
        scrollPosition: {
          x: 0,
          y: 0,
        },
      }
    },
    computed: {
      contentWidth: function () {
        let margin = 10;
        return this.containerWidth - margin;
      },
      containerClass: function () {
        if (this.cursorMode != "hand") return null;
        return "cursor-grab";
      },
    },
    props: {
      // PDFファイルのURL（オリジンが同一である必要があります）
      url: {
        type: String,
        required: true,
        defalult: null,
      },
      // ダウンロード時のファイル名
      fileName: {
        type: String,
        required: true,
        defalult: null,
      },
      // 初期表示ページ
      initialPage: {
        type: Number,
        required: false,
        defalult: 1,
      },
      // ズーム（10 - 100）
      zoom: {
        type: Number,
        required: false,
        default: 100,
      },
      cursorMode: {
        type: String,
        default: "cursor",
        validator: (val) => {
          return ["cursor", "hand"].includes(val);
        },
      },
      scrollContainer: {
        type: Object,
        required: false,
        default: document.documentElement,
      },
      workerSrc: {
        type: String,
        require: true,
      },
      onCurrentPageChange: {
        type: Function,
        require: false,
      },
      onTotalPagesChange: {
        type: Function,
        require: false,
      },
      pages: {
        type: Number,
        default: 1,
      },
      mode: {
        type: String,
        default: "continuous",
        validator: (val) => {
          return [null, "single", "continuous"].includes(val);
        },
      },
    },
    watch: watchers,
    methods: methods,
    created: onCreated,
    mounted: onMounted,
    destroyed: onDestroyed,
  });

</script>

<style scoped>
  .cursor-grab {
    cursor: grab;
  }
  .cursor-grabbing {
    cursor: grabbing;
  }
</style>
